import { useEffect, useState, useCallback } from "react";
import { Gift, Copy, Check, ShareNetwork } from "@phosphor-icons/react";
import { Capacitor } from "@capacitor/core";
import { api, formatApiError } from "@/lib/api";

/**
 * ReferralCard — invite friends; both sides get 30 days of Hush Plus.
 *
 *  - Shows the user's unique referral code + share link (auto-minted by the
 *    server on first visit if missing).
 *  - "Copy link" / "Share" buttons (native Share API + Capacitor.Share fallback).
 *  - Stats row: referrals_count + active credit expiry.
 *  - "Have a code?" redeem input for users who didn't enter one at signup.
 */
export default function ReferralCard({ onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");
  const [redeemErr, setRedeemErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/referrals/me");
      setData(r);
    } catch (_) {
      // Silent — referral feature is opt-in surface, not critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = async () => {
    if (!data?.referral_link) return;
    try {
      await navigator.clipboard.writeText(data.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Fallback: select-and-copy on older browsers
      const el = document.createElement("textarea");
      el.value = data.referral_link;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) { /* noop */ }
      document.body.removeChild(el);
    }
  };

  const share = async () => {
    if (!data?.referral_link) return;
    const text = `I've been using Hush — a quiet AI voice journal that picks up on how you're really feeling. Here's 30 days of Hush Plus on me: ${data.referral_link}`;
    // Native Capacitor share first (better UX on iOS/Android)
    if (Capacitor.isNativePlatform()) {
      try {
        const { Share } = await import("@capacitor/share");
        await Share.share({ title: "Hush — a quiet voice journal", text, url: data.referral_link });
        return;
      } catch (_) { /* fall through to web share */ }
    }
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: "Hush", text, url: data.referral_link }); return; }
      catch (_) { /* noop */ }
    }
    copyLink();
  };

  const redeem = async (e) => {
    e?.preventDefault?.();
    setRedeemErr(""); setRedeemMsg("");
    const code = (redeemCode || "").trim().toUpperCase();
    if (!code) return;
    setRedeemBusy(true);
    try {
      const { data: r } = await api.post("/referrals/redeem", { code });
      setRedeemMsg(`Nice — ${r.granted_days} days of Hush Plus unlocked.`);
      setRedeemCode("");
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setRedeemErr(formatApiError(err));
    } finally {
      setRedeemBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-5 mb-5" data-testid="referral-card-loading">
        <p className="eyebrow text-ink-500">Invite friends</p>
        <p className="text-sm text-ink-500 mt-2">Loading…</p>
      </div>
    );
  }
  if (!data) return null;

  const reward = data.reward_days_per_referral || 30;

  return (
    <div className="card p-5 mb-5 grain" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-1">
        <Gift size={20} weight="fill" className="text-brand" />
        <p className="eyebrow text-brand">Invite friends · earn Plus</p>
      </div>
      <h3 className="font-serif text-lg text-ink-900 leading-tight mb-1">
        Give {reward} days of Plus, get {reward} days back.
      </h3>
      <p className="text-xs text-ink-500 mb-4">
        When a friend signs up with your code, you both get {reward} days of Hush Plus, free.
      </p>

      {/* Share row */}
      <div className="flex items-stretch gap-2 mb-3">
        <div className="flex-1 min-w-0 rounded-md border border-bone-300 bg-bone-100 px-3 py-2">
          <p className="text-[10px] uppercase tracking-widest text-ink-500">Your code</p>
          <p
            className="font-mono text-base text-ink-900 truncate"
            data-testid="referral-code-value"
          >
            {data.referral_code}
          </p>
        </div>
        <button
          type="button"
          onClick={copyLink}
          className="btn-ghost text-xs px-3 inline-flex items-center gap-1.5"
          data-testid="referral-copy-link-btn"
          aria-label="Copy invite link"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy link"}
        </button>
        <button
          type="button"
          onClick={share}
          className="btn-primary text-xs px-3 inline-flex items-center gap-1.5"
          data-testid="referral-share-btn"
          aria-label="Share invite"
        >
          <ShareNetwork size={14} /> Share
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-md bg-bone-100 border border-bone-300 p-3">
          <p className="text-[10px] uppercase tracking-widest text-ink-500">Friends joined</p>
          <p className="font-serif text-2xl mt-1" data-testid="referral-count">{data.referrals_count}</p>
        </div>
        <div className="rounded-md bg-bone-100 border border-bone-300 p-3">
          <p className="text-[10px] uppercase tracking-widest text-ink-500">
            {data.credit_active ? "Plus credit until" : "Plus credit"}
          </p>
          <p className="font-serif text-base mt-1" data-testid="referral-credit-until">
            {data.credit_active && data.credit_until
              ? new Date(data.credit_until).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
              : "—"}
          </p>
        </div>
      </div>

      {/* Redeem row — only if not yet redeemed */}
      {!data.redeemed_code && !redeemMsg && (
        <form onSubmit={redeem} className="mt-4 pt-4 border-t border-bone-300">
          <label className="eyebrow block mb-2">Have a friend's code?</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              maxLength={16}
              className="input font-mono uppercase"
              data-testid="referral-redeem-input"
            />
            <button
              type="submit"
              disabled={redeemBusy || !redeemCode.trim()}
              className="btn-primary text-xs px-4 disabled:opacity-50"
              data-testid="referral-redeem-btn"
            >
              {redeemBusy ? "…" : "Redeem"}
            </button>
          </div>
          {redeemErr && (
            <p className="text-xs text-brand mt-2" data-testid="referral-redeem-error">{redeemErr}</p>
          )}
        </form>
      )}
      {redeemMsg && (
        <div
          className="mt-4 pt-4 border-t border-bone-300 flex items-start gap-2 text-sm text-moss"
          data-testid="referral-redeem-success"
        >
          <Check size={16} weight="bold" className="shrink-0 mt-0.5" />
          <span>{redeemMsg}</span>
        </div>
      )}
    </div>
  );
}
